import TextField from "./CustomTextField";
import { gql, useQuery } from "@apollo/client";
import {
  Alert,
  Checkbox,
  MenuItem,
  Box,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import UiSideModal from "components/base/UiSideModal";
import UiButton from "components/base/UiButton";
import { CostRow, money, truckingUnits, TruckingUnit } from "./calculator";
import { libraryCostRow, LibraryItem } from "./library";

const queries = {
  trucking: gql`
    query CustomTransferTruckLibrary {
      trucks: getTransferTrucks {
        id
        name
        defaultTonnage
        defaultRateHourly
        defaultRateTon
      }
    }
  `,
  labor: gql`
    query CustomCrewLibrary($search: String, $page: Int!) {
      library: getCrews(search: $search, page: $page, first: 20, sort: ASC) {
        data {
          id
          name
          rate
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  `,
  equipment: gql`
    query CustomEquipmentLibrary($search: String, $page: Int!) {
      library: getEquipmentPieces(search: $search, page: $page, first: 20, sort: ASC) {
        data {
          id
          name
          rate
          chargeType
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  `,
  other: gql`
    query CustomOtherCostLibrary($search: String, $page: Int!) {
      library: getAdditionalCosts(
        search: $search
        page: $page
        first: 20
        sort: ASC
        feeType: FLAT
      ) {
        data {
          id
          name
          cost
          feeAmount
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  `,
};
export type LibraryCategory = keyof typeof queries;
const titles = {
  trucking: "Add Transfer Truck",
  labor: "Add Crew",
  equipment: "Add Equipment",
  other: "Add Other Cost",
};
export default function CustomLibraryDrawer({
  category,
  onClose,
  onAdd,
  disabled,
}: {
  category: LibraryCategory;
  onClose: () => void;
  onAdd: (rows: CostRow[]) => void;
  disabled: boolean;
}) {
  const multiple = category !== "trucking";
  const [selectedItems, setSelectedItems] = useState<Record<string, LibraryItem>>({});
  const [truckUnit, setTruckUnit] = useState<TruckingUnit>("Hours");
  const [truckRate, setTruckRate] = useState("");
  const needsRate = category === "trucking" && ["Yards", "Loads"].includes(truckUnit);
  const validRate = /^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(truckRate);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, loading, error, refetch } = useQuery(queries[category], {
    variables: { search: debounced, page },
    fetchPolicy: "network-only",
  });
  const pending = loading || search !== debounced;
  const trucks: LibraryItem[] = (data?.trucks ?? []).filter((item: LibraryItem) =>
    item.name.toLowerCase().includes(debounced.toLowerCase())
  );
  const items: LibraryItem[] =
    category === "trucking" ? trucks.slice((page - 1) * 20, page * 20) : data?.library?.data ?? [];
  const more =
    category === "trucking" ? trucks.length > page * 20 : data?.library?.paginatorInfo.hasMorePages;
  const current = items.find((item) => item.id === selected);
  return (
    <UiSideModal
      open
      onClose={onClose}
      title={titles[category]}
      width="598px"
      footer={
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
        >
          <Typography variant="body2" aria-live="polite">
            {multiple ? `${Object.keys(selectedItems).length} selected` : ""}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <UiButton uiVariant="outlined-small" onClick={onClose}>
              Cancel
            </UiButton>
            <UiButton
              uiVariant="primary-green"
              disabled={
                (multiple ? !Object.keys(selectedItems).length : !current) ||
                pending ||
                !!error ||
                disabled ||
                (needsRate && !validRate)
              }
              onClick={() => {
                const chosen = multiple ? Object.values(selectedItems) : current ? [current] : [];
                if (chosen.length) {
                  onAdd(
                    chosen.map((entry) => libraryCostRow(category, entry, truckUnit, truckRate))
                  );
                  onClose();
                }
              }}
            >
              {multiple ? "Save" : "Add"}
            </UiButton>
          </Box>
        </Box>
      }
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          ...(multiple
            ? { height: "100%", minHeight: 0, boxSizing: "border-box", "& > *": { flexShrink: 0 } }
            : {}),
        }}
      >
        {category === "equipment" && (
          <Alert
            severity="info"
            sx={{
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              "& .MuiAlert-message": {
                minWidth: 0,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                fontSize: 13,
                lineHeight: 1.5,
              },
            }}
          >
            Uses the base rate only. Fuel surcharge, utilization, and travel settings are excluded.
          </Alert>
        )}
        {category === "other" && <Typography variant="body2">Flat Fee costs only.</Typography>}
        <TextField
          size="small"
          label="Search library"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setSelected("");
          }}
        />
        {category === "trucking" && (
          <>
            <TextField
              select
              size="small"
              label="Billing unit"
              value={truckUnit}
              onChange={(e) => {
                setTruckUnit(e.target.value as TruckingUnit);
                setTruckRate("");
              }}
            >
              {truckingUnits.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </TextField>
            {needsRate && (
              <TextField
                size="small"
                label={`Rate per ${truckUnit === "Loads" ? "load" : "cubic yard"}`}
                value={truckRate}
                inputProps={{ inputMode: "decimal" }}
                onChange={(e) => setTruckRate(e.target.value)}
                helperText="Enter a rate; this unit has no saved library rate."
              />
            )}
            <Typography variant="body2">
              Tons are US tons; Yards are cubic yards. Truck capacity is reference only.
            </Typography>
          </>
        )}
        {error && (
          <Alert
            severity="error"
            action={
              <UiButton uiVariant="outlined-small" onClick={() => refetch()}>
                Retry
              </UiButton>
            }
          >
            {error.message}
          </Alert>
        )}
        <Box
          sx={
            multiple
              ? { flex: "1 1 0 !important", minHeight: 120, overflowY: "auto", overflowX: "hidden" }
              : {}
          }
        >
          {pending ? (
            <CircularProgress size={24} aria-label="Loading library" />
          ) : (
            !error && (
              <>
                {!items.length && <Typography>No matching library items.</Typography>}
                <Box
                  component={multiple ? "div" : RadioGroup}
                  aria-label="Library items"
                  {...(!multiple
                    ? {
                        value: selected,
                        onChange: (_: unknown, value: string) => setSelected(value),
                      }
                    : {})}
                >
                  {items.map((item) => {
                    const row = libraryCostRow(category, item, truckUnit, truckRate);
                    return (
                      <FormControlLabel
                        key={item.id}
                        value={item.id}
                        disabled={disabled}
                        sx={{
                          m: 0,
                          width: "100%",
                          minHeight: 44,
                          "& .MuiFormControlLabel-label": {
                            fontSize: 13,
                            overflowWrap: "anywhere",
                          },
                        }}
                        control={
                          multiple ? (
                            <Checkbox
                              size="small"
                              checked={!!selectedItems[item.id]}
                              onChange={(_, checked) =>
                                setSelectedItems((previous) => {
                                  const next = { ...previous };
                                  if (checked) next[item.id] = item;
                                  else delete next[item.id];
                                  return next;
                                })
                              }
                            />
                          ) : (
                            <Radio size="small" />
                          )
                        }
                        label={`${item.name} — ${
                          row.rate === "" ? "Enter rate" : money(Number(row.rate) * 100)
                        } / ${row.unit}${
                          category === "trucking"
                            ? ` · Capacity ${item.defaultTonnage ?? 0} US tons`
                            : row.quantity !== "1"
                            ? ` · Quantity ${row.quantity}`
                            : ""
                        }`}
                      />
                    );
                  })}
                </Box>
              </>
            )
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <UiButton
            uiVariant="outlined-small"
            disabled={pending || page === 1}
            onClick={() => {
              setPage(page - 1);
              setSelected("");
            }}
          >
            Previous
          </UiButton>
          <Typography variant="body2">Page {page}</Typography>
          <UiButton
            uiVariant="outlined-small"
            disabled={pending || !more}
            onClick={() => {
              setPage(page + 1);
              setSelected("");
            }}
          >
            Next
          </UiButton>
        </Box>
      </Box>
    </UiSideModal>
  );
}
